import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

const pastelColors = [
  '#ccb4d6', // soft purple
  '#f8e1f4', // pink
  '#b4e7f8', // blue
  '#f8e7b4', // yellow
  '#e1f8d6', // green
  '#f8b4c4', // rose
];

const RedeemCode = () => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Load font and inject keyframes for background animation & title wave
    const fontLink = document.createElement('link');
    fontLink.rel = 'stylesheet';
    fontLink.href = 'https://fonts.googleapis.com/css2?family=Quicksand:wght@700&family=Great+Vibes&display=swap';
    document.head.appendChild(fontLink);
    const style = document.createElement('style');
    style.textContent = `
      @keyframes pastelBg {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
      @keyframes floatBlob {
        0% { transform: translateY(0) scale(1); }
        50% { transform: translateY(-20px) scale(1.08); }
        100% { transform: translateY(0) scale(1); }
      }
      @keyframes inputGlow {
        0%, 100% { box-shadow: 0 0 0 0 #ccb4d6; }
        50% { box-shadow: 0 0 16px 4px #ccb4d6; }
      }
      @keyframes titleWave {
        0% { transform: translateY(0); }
        50% { transform: translateY(-8px); }
        100% { transform: translateY(0); }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(fontLink);
      document.head.removeChild(style);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      Swal.fire({ icon: 'warning', title: 'Code must be 6 digits', confirmButtonColor: pastelColors[0] });
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('https://api.laviopaperie.com/api/redeem-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code })
      });
      const data = await res.json();
      setLoading(false);
      if (data.success) {
        // Simpan code ke localStorage untuk digunakan saat POST photo-session
        localStorage.setItem('redeemedCode', code);
        navigate('/frames');
      } else {
        Swal.fire({ icon: 'error', title: 'Wrong Code', text: data.message || 'Invalid code, try again.', confirmButtonColor: pastelColors[0] });
      }
    } catch (err) {
      setLoading(false);
      Swal.fire({ icon: 'error', title: 'Server error', text: 'Coba lagi nanti', confirmButtonColor: pastelColors[0] });
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      width: '100vw',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: `linear-gradient(120deg, ${pastelColors.join(', ')})`,
      backgroundSize: '300% 300%',
      animation: 'pastelBg 18s ease-in-out infinite',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Animated floating blobs */}
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 0, pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', top: '10%', left: '8%', width: 180, height: 180, background: 'radial-gradient(circle, #ccb4d6 60%, transparent)', filter: 'blur(18px)', opacity: 0.7, animation: 'floatBlob 7s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', bottom: '12%', right: '10%', width: 140, height: 140, background: 'radial-gradient(circle, #b4e7f8 60%, transparent)', filter: 'blur(16px)', opacity: 0.6, animation: 'floatBlob 9s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', top: '60%', left: '60%', width: 120, height: 120, background: 'radial-gradient(circle, #f8e1f4 60%, transparent)', filter: 'blur(14px)', opacity: 0.5, animation: 'floatBlob 11s ease-in-out infinite' }} />
      </div>
      {/* Main Card */}
      <div style={{
        background: 'rgba(255,255,255,0.85)',
        borderRadius: 24,
        boxShadow: '0 8px 32px 0 rgba(204,180,214,0.18)',
        padding: '48px 32px',
        maxWidth: 600,
        width: '90vw',
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        zoom: '80%',
      }}>
        <div style={{ fontFamily: 'Great Vibes, cursive', fontSize: 38, color: '#a28ad6', marginBottom: 8, letterSpacing: 1, textShadow: '0 2px 8px #ccb4d6' }}>
          {Array.from('Lavio Paperie').map((ch, i) => (
            <span
              key={i}
              style={{
                display: 'inline-block',
                animation: 'titleWave 2.2s ease-in-out infinite',
                animationDelay: `${i * 0.06}s`,
                willChange: 'transform',
                fontSize: '1.1em',
              }}
            >
               {ch === ' ' ? '\u00A0' : ch}
            </span>
          ))}
        </div>

        {/* Deskripsi Produk Animated */}
        <div
          style={{
            fontFamily: 'Quicksand, sans-serif', fontWeight: 800, color: '#7c5fa6', fontSize: 15, marginBottom: 12,
            textAlign: 'center',
            animation: 'fadeInDesc 1.2s cubic-bezier(0.16, 1, 0.3, 1)',
            letterSpacing: 1,
            padding: '6px 0 2px 0'
          }}
        >
          {/* A moment can pass fast, but the way you capture it doesn’t have to.<br /><br /> */}
          With Lavio Paperie, your snapshots turn into little stories that keep every memory beautifully alive.<br />
          <span style={{ display: 'block', marginTop: 12, marginBottom: 5, fontStyle: 'italic', color: '#b48ad6', fontWeight: 600, fontSize: 15 }}>
            “From concept to creation — your special moments become our canvas.”
          </span>
          <span style={{ fontWeight: 900, color: '#a28ad6', fontSize: 19, letterSpacing: 1, display: 'block', marginTop: 2 }}>Rp 36.000 / session</span>
        </div>
        {/* CSS FadeIn Desc animasi langsung di-head pakai useEffect */}
        {/* Tulisan "atau" center animated */}
        <div style={{ width: '100%', display: 'flex', alignItems: 'center', margin: '6px 0 4px 0', justifyContent: 'center' }}>
          <div style={{ flex: 1, borderTop: '1px solid #e0d1ea', marginRight: 6, opacity: 0.56 }} />
          <div style={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 600, color: '#b48ad6', fontSize: 15, letterSpacing: 2, animation: 'fadeInDesc 1.6s .4s both' }}>or</div>
          <div style={{ flex: 1, borderTop: '1px solid #e0d1ea', marginLeft: 6, opacity: 0.56 }} />
        </div>
        {/* Form Redeem Code */}
        <div style={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 700, fontSize: 22, color: '#7c5fa6', marginBottom: 18, letterSpacing: 2 }}>
          Enter Code
        </div>
        <form onSubmit={handleSubmit} style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 2, marginBottom: 4 }} autoComplete="off">
          <input
            type="text"
            maxLength={6}
            value={code}
            onChange={e => setCode(e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase())}
            placeholder="6 Digit Code"
            style={{
              width: '100%',
              fontSize: 26,
              letterSpacing: 8,
              textAlign: 'center',
              padding: '12px 0',
              borderRadius: 12,
              border: '2px solid #ccb4d6',
              outline: 'none',
              marginBottom: 14,
              background: '#f8f4ff',
              color: '#7c5fa6',
              fontFamily: 'Quicksand, sans-serif',
              fontWeight: 700,
              boxShadow: '0 2px 8px 0 rgba(204,180,214,0.07)',
              animation: 'inputGlow 2.5s infinite',
              transition: 'border 0.2s',
            }}
            disabled={loading}
            autoFocus
          />
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px 0',
              borderRadius: 999,
              border: 'none',
              background: loading ? 'linear-gradient(90deg, #ccb4d6 0%, #b4e7f8 100%)' : 'linear-gradient(90deg, #a28ad6 0%, #f8e1f4 100%)',
              color: '#fff',
              fontSize: 19,
              fontWeight: 700,
              letterSpacing: 2,
              boxShadow: '0 4px 16px 0 rgba(204,180,214,0.17)',
              cursor: loading ? 'not-allowed' : 'pointer',
              marginBottom: 2,
              marginTop: 0,
              transition: 'all 0.2s',
            }}
          >
            {loading ? 'Checking...' : 'Redeem Code'}
          </button>
        </form>
        {/* Below Form: Syarat, Kebijakan, Disclaimer, Kontak Business */}
        <div style={{ background: '#f2eefd', borderRadius: 11, boxShadow: '0 1px 4px 0 rgba(204,180,214,0.03)', padding: "8px 12px", fontFamily: 'Quicksand, sans-serif', color: '#825fa6', fontSize: 12.7, marginBottom: 8, width: '100%' }}>
          <div style={{ fontWeight: 700, color: '#9a6be6', marginBottom: 2 }}>Syarat & Ketentuan:</div>
          <ul style={{ paddingLeft: 18, marginBottom: 4, marginTop: 0 }}>
            <li>Kode hanya dapat digunakan satu kali dan berlaku selama 1 bulan sejak kode diberikan atau diterima.</li>
            <li>Pembayaran non-refundable, kecuali layanan gagal diberikan.</li>
            <li>Foto dikirim via email.</li>
          </ul>
          <div style={{ fontWeight: 700, color: '#b48ad6', marginBottom: 2 }}>Kebijakan Pengembalian Dana & Produk:</div>
          <div>Jika terjadi kendala teknis, refund penuh diberikan max. 7 hari kerja.</div>
        </div>
        <div style={{ background: '#f8f6ff', borderRadius: 9, boxShadow: '0 0px 2px 0 rgba(204,180,214,0.03)', padding: '6px 10px', fontFamily: 'Quicksand, sans-serif', color: '#7c5fa6', fontSize: 12.5, marginBottom: 7, width: '100%' }}>
          <b style={{ color: '#ae8ee2' }}>Disclaimer:</b> Layanan, harga, serta syarat & ketentuan dapat berubah tanpa pemberitahuan. Jika terjadi gangguan teknis, refund diproses sesuai kebijakan. Untuk info terbaru, silakan hubungi Lavio Paperie.
        </div>
        <div style={{ background: '#e9f7f7', borderRadius: 10, padding: '7px 9px', textAlign: 'center', fontSize: 12.5, color: '#697ba9', fontFamily: 'Quicksand, sans-serif', boxShadow: '0 1px 3px 0 rgba(176,195,198,0.05)', width: '100%' }}>
          <b style={{ color: '#24b3b3' }}>Our Contact:</b><br />
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2, marginBottom: 1, justifyContent: 'center' }}>
            <img width="40" height="40" src="https://img.icons8.com/clouds/100/gmail-new.png" alt="gmail-new" />
            <span style={{ marginRight: 4 }} role="img" aria-label="instagram">Email:</span>
            <a style={{ color: '#21a8a8', textDecoration: 'underline', marginRight: 10 }} href="mailto:lavio.paperie@gmail.com">lavio.paperie@gmail.com</a>
          <img width="40" height="40" src="https://img.icons8.com/clouds/100/instagram-new--v3.png" alt="instagram-new--v3" style={{ verticalAlign: 'middle' }} />
            <span style={{ marginRight: 4 }} role="img" aria-label="instagram">Instagram:</span>
            <a style={{ color: '#21a8a8', textDecoration: 'underline' }} href="https://instagram.com/lavio.paperie" target="_blank" rel="noopener noreferrer">@lavio.paperie</a>
          </div>
        </div>
        <style>{`@keyframes fadeInDesc{from{opacity:0;transform:translateY(-13px)}to{opacity:1;transform:translateY(0)}}`}</style>

      </div>
    </div>
  );
};

export default RedeemCode;
